import React, { useEffect, useState, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, Loader2, AlertCircle, Sparkles, ArrowRight } from 'lucide-react';
import { getCheckoutSession } from '../lib/billingClient';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../hooks/useSubscription';
import { PLANS, formatBRL } from '../lib/plans';

const POLL_INTERVAL_MS = 2500;
const MAX_ATTEMPTS = 20; // ~50s total

const BillingSuccess = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const { refresh } = useSubscription();
  const sessionId = params.get('session_id');

  const [status, setStatus] = useState('polling'); // polling | success | pending_pix | error | timeout
  const [session, setSession] = useState(null);
  const [error, setError] = useState('');
  const attemptsRef = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      setError('session_id ausente na URL.');
      return;
    }

    const poll = async () => {
      attemptsRef.current += 1;
      try {
        const data = await getCheckoutSession(sessionId);
        setSession(data);
        // session.payment_status: 'paid' | 'unpaid' | 'no_payment_required'
        // session.subscription_status: 'active' | 'trialing' | 'incomplete' | ...
        if (data.payment_status === 'paid' || data.subscription_status === 'active' || data.subscription_status === 'trialing') {
          setStatus('success');
          try { await refresh(); } catch { /* noop */ }
          return; // stop polling
        }
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          // For PIX, payment confirmation might take longer (customer needs to pay)
          setStatus('pending_pix');
          return;
        }
        timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        if (attemptsRef.current >= 3) {
          setStatus('error');
          setError(e.message || 'Falha ao confirmar pagamento.');
          return;
        }
        timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
      }
    };

    poll();
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const planId = session?.plan_id;
  const plan = planId ? PLANS[planId] : null;

  return (
    <div className="min-h-screen bg-[#050816] text-white flex items-center justify-center px-4 py-10" data-testid="billing-success-page">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-card rounded-3xl p-7 sm:p-10 max-w-md w-full text-center"
      >
        {status === 'polling' && (
          <>
            <Loader2 size={42} className="mx-auto text-[#4facfe] animate-spin" />
            <h1 className="heading-display text-xl sm:text-2xl font-bold mt-5">Confirmando seu pagamento...</h1>
            <p className="text-sm text-slate-400 mt-2">
              Estamos sincronizando sua assinatura com o Stripe. Isso leva apenas alguns segundos.
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 220 }}>
              <CheckCircle2 size={56} className="mx-auto text-emerald-400" />
            </motion.div>
            <h1 className="heading-display text-2xl font-bold mt-5">
              Deu <span className="gradient-text">Spark</span>! 🎉
            </h1>
            <p className="text-sm text-slate-300 mt-2">
              {plan ? (
                <>Seu plano <span className="font-bold text-white">{plan.name}</span> está ativo.</>
              ) : (
                'Sua assinatura foi ativada com sucesso.'
              )}
            </p>
            {plan && (
              <div className="mt-5 inline-flex items-baseline gap-1.5">
                <span className="heading-display text-3xl font-bold gradient-text">{plan.price_label}</span>
                <span className="text-xs text-slate-400">{plan.period_label}</span>
              </div>
            )}
            <div className="mt-7 flex flex-col gap-2.5">
              <Link to="/app/billing" data-testid="success-go-billing" className="btn-primary rounded-xl py-3 text-sm font-bold inline-flex items-center justify-center gap-2">
                Ver minha assinatura <ArrowRight size={14} />
              </Link>
              <Link to="/app/feed" data-testid="success-go-feed" className="text-xs text-slate-400 hover:text-white transition-colors py-2">
                Voltar ao Feed
              </Link>
            </div>
          </>
        )}

        {status === 'pending_pix' && (
          <>
            <Sparkles size={42} className="mx-auto text-[#ffa726]" />
            <h1 className="heading-display text-xl font-bold mt-5">Pagamento aguardando confirmação</h1>
            <p className="text-sm text-slate-300 mt-2">
              Se você escolheu <span className="font-bold text-white">PIX</span>, conclua o pagamento no app do seu banco.
              Assim que confirmarmos o recebimento, seu plano será ativado automaticamente — pode levar até alguns minutos.
            </p>
            {session?.amount_total != null && (
              <div className="mt-5 inline-flex items-baseline gap-1.5">
                <span className="heading-display text-2xl font-bold text-white">{formatBRL(session.amount_total)}</span>
              </div>
            )}
            <div className="mt-7 flex flex-col gap-2.5">
              <Link to="/app/billing" data-testid="success-go-billing-pix" className="btn-primary rounded-xl py-3 text-sm font-bold inline-flex items-center justify-center gap-2">
                Acompanhar status <ArrowRight size={14} />
              </Link>
            </div>
          </>
        )}

        {status === 'error' && (
          <>
            <AlertCircle size={42} className="mx-auto text-red-400" />
            <h1 className="heading-display text-xl font-bold mt-5">Não conseguimos confirmar o pagamento</h1>
            <p className="text-sm text-slate-300 mt-2">{error || 'Tente recarregar esta página em alguns segundos.'}</p>
            <div className="mt-7 flex flex-col gap-2.5">
              <button onClick={() => window.location.reload()} data-testid="success-retry" className="btn-primary rounded-xl py-3 text-sm font-bold">
                Tentar novamente
              </button>
              <Link to="/app/plans" className="text-xs text-slate-400 hover:text-white transition-colors py-2">
                Voltar aos planos
              </Link>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default BillingSuccess;
